import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiClock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiX,
} from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};
const fieldOrder = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "subject",
  "message",
];

function Contact() {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimer = useRef(null);
  const successTimer = useRef(null);
  const successMessage = useRef(null);

  useEffect(
    () => () => {
      window.clearTimeout(submitTimer.current);
      window.clearTimeout(successTimer.current);
    },
    []
  );

  const validateField = (name, value) => {
    const cleanValue = value.trim();
    if (name === "firstName" && !cleanValue)
      return "Please enter your first name.";
    if (name === "lastName" && !cleanValue)
      return "Please enter your last name.";
    if (name === "email") {
      if (!cleanValue) return "Please enter your email address.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue))
        return "Enter an email address in the format name@example.com.";
    }
    if (
      name === "phone" &&
      cleanValue &&
      cleanValue.replace(/\D/g, "").length < 7
    )
      return "Enter a complete phone number or leave this field empty.";
    if (name === "subject" && !cleanValue) return "Please choose a topic.";
    if (name === "message") {
      if (!cleanValue) return "Please tell us how we can help.";
      if (cleanValue.length < 10)
        return "Please add a little more detail (at least 10 characters).";
    }
    return "";
  };

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    if (showSuccess) setShowSuccess(false);
    if (errors[name])
      setErrors((current) => ({
        ...current,
        [name]: validateField(name, value),
      }));
  };

  const handleBlur = ({ target: { name, value } }) => {
    setErrors((current) => ({
      ...current,
      [name]: validateField(name, value),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = fieldOrder.reduce((result, name) => {
      const error = validateField(name, formData[name]);
      if (error) result[name] = error;
      return result;
    }, {});

    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      event.currentTarget.elements[Object.keys(formErrors)[0]]?.focus();
      return;
    }

    setIsSubmitting(true);
    setShowSuccess(false);
    setErrors({});
    window.clearTimeout(submitTimer.current);
    window.clearTimeout(successTimer.current);
    submitTimer.current = window.setTimeout(() => {
      setFormData(emptyForm);
      setIsSubmitting(false);
      setShowSuccess(true);
      window.requestAnimationFrame(() => successMessage.current?.focus());
      successTimer.current = window.setTimeout(
        () => setShowSuccess(false),
        8000
      );
    }, 900);
  };

  const faqItems = [
    [
      "When will I hear back?",
      `Our customer care team is available ${siteConfig.contact.hours}. We usually reply within two working days, with a little extra time during collection launches.`,
    ],
    [
      "Can I change or cancel my order?",
      "Send us your order number as soon as possible. If your parcel has not entered preparation, we will do our best to update it before dispatch.",
    ],
    [
      "How do returns work?",
      `Unworn pieces can be returned with their original tags within ${siteConfig.commerce.returnWindow} days of delivery. Visit our Returns page for the full steps and conditions.`,
    ],
    [
      "Where can I find delivery information?",
      "Our Shipping page covers delivery times, tracking and rates. Once your order leaves our warehouse, we will also send a tracking link by email.",
    ],
  ];

  const fieldProps = (name) => ({
    name,
    value: formData[name],
    onChange: handleInputChange,
    onBlur: handleBlur,
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
    disabled: isSubmitting,
  });

  const fieldError = (name) =>
    errors[name] && (
      <p id={`${name}-error`} className="contact-field__error">
        {errors[name]}
      </p>
    );

  return (
    <main className="contact-page">
      <section
        className="contact-hero page-shell"
        aria-labelledby="contact-title"
      >
        <div className="contact-hero__mark" aria-hidden="true">
          {siteConfig.logo.symbol}
        </div>
        <div className="contact-hero__heading">
          <p className="eyebrow">Customer care</p>
          <h1 id="contact-title">
            We are here
            <br />
            to help.
          </h1>
        </div>
        <div className="contact-hero__intro">
          <p>
            Fit question, order update or something you would simply like to
            share? Send us a note and a member of the {siteConfig.brandName}{" "}
            team will get back to you.
          </p>
          <span>Real people, thoughtful answers.</span>
        </div>
      </section>

      <section
        className="contact-workspace page-shell"
        aria-label="Contact options and message form"
      >
        <aside
          className="contact-details"
          aria-labelledby="contact-details-title"
        >
          <p className="eyebrow">Other ways to reach us</p>
          <h2 id="contact-details-title">Choose your channel.</h2>
          <address className="contact-details__list">
            <a href={`mailto:${siteConfig.contact.email}`}>
              <span>
                <FiMail aria-hidden="true" />
              </span>
              <span>
                <small>Email</small>
                <strong>{siteConfig.contact.email}</strong>
              </span>
            </a>

            <div>
              <span>
                <FiMapPin aria-hidden="true" />
              </span>
              <span>
                <small>Studio</small>
                <strong>{siteConfig.contact.address}</strong>
              </span>
            </div>
            <div>
              <span>
                <FiClock aria-hidden="true" />
              </span>
              <span>
                <small>Service hours</small>
                <strong>{siteConfig.contact.hours}</strong>
              </span>
            </div>
          </address>
          <div className="contact-details__links">
            <p className="eyebrow">Quick answers</p>
            <Link to="/shipping">
              Shipping <FiArrowRight aria-hidden="true" />
            </Link>
            <Link to="/returns">
              Returns & exchanges <FiArrowRight aria-hidden="true" />
            </Link>
            <Link to="/privacy">
              Privacy <FiArrowRight aria-hidden="true" />
            </Link>
          </div>
        </aside>

        <div className="contact-form-card">
          <div className="contact-form-card__heading">
            <p className="eyebrow">Send a message</p>
            <h2>How can we help?</h2>
            <p>Fields marked with an asterisk are required.</p>
          </div>
          {showSuccess && (
            <div
              className="contact-success"
              role="status"
              tabIndex="-1"
              ref={successMessage}
            >
              <div>
                <strong>Thank you for getting in touch.</strong>
                <p>
                  Your message is on its way to our customer care team. We will
                  reply as soon as we can.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowSuccess(false)}
                aria-label="Dismiss confirmation"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
          )}
          <form
            className="contact-form"
            onSubmit={handleSubmit}
            noValidate
            aria-busy={isSubmitting}
          >
            <div className="contact-form__row">
              <div
                className={`contact-field${
                  errors.firstName ? " has-error" : ""
                }`}
              >
                <label htmlFor="contact-first-name">
                  First name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="contact-first-name"
                  type="text"
                  autoComplete="given-name"
                  required
                  {...fieldProps("firstName")}
                />
                {fieldError("firstName")}
              </div>
              <div
                className={`contact-field${
                  errors.lastName ? " has-error" : ""
                }`}
              >
                <label htmlFor="contact-last-name">
                  Last name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="contact-last-name"
                  type="text"
                  autoComplete="family-name"
                  required
                  {...fieldProps("lastName")}
                />
                {fieldError("lastName")}
              </div>
            </div>
            <div className="contact-form__row">
              <div
                className={`contact-field${errors.email ? " has-error" : ""}`}
              >
                <label htmlFor="contact-email">
                  Email address <span aria-hidden="true">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  {...fieldProps("email")}
                />
                {fieldError("email")}
              </div>
              <div
                className={`contact-field${errors.phone ? " has-error" : ""}`}
              >
                <label htmlFor="contact-phone">
                  Phone <small>Optional</small>
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  {...fieldProps("phone")}
                />
                {fieldError("phone")}
              </div>
            </div>
            <div
              className={`contact-field${errors.subject ? " has-error" : ""}`}
            >
              <label htmlFor="contact-subject">
                What can we help with? <span aria-hidden="true">*</span>
              </label>
              <select id="contact-subject" required {...fieldProps("subject")}>
                <option value="">Select a topic</option>
                <option value="order">My order</option>
                <option value="delivery">Delivery or tracking</option>
                <option value="return">Return or exchange</option>
                <option value="product">Product, fit or care</option>
                <option value="other">Something else</option>
              </select>
              {fieldError("subject")}
            </div>
            <div
              className={`contact-field contact-field--message${
                errors.message ? " has-error" : ""
              }`}
            >
              <label htmlFor="contact-message">
                Your message <span aria-hidden="true">*</span>
              </label>
              <textarea
                id="contact-message"
                rows="6"
                maxLength="1200"
                required
                {...fieldProps("message")}
                aria-describedby={`message-note${
                  errors.message ? " message-error" : ""
                }`}
              />
              <div className="contact-field__meta">
                <span id="message-note">
                  Please do not include payment details.
                </span>
                <span>{formData.message.length} / 1200</span>
              </div>
              {fieldError("message")}
            </div>
            <p className="contact-form__privacy">
              By sending this form, you agree that we may use your details to
              answer your request. Read our{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
            <button
              className="button-primary contact-form__submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="contact-form__spinner" aria-hidden="true" />{" "}
                  Sending message…
                </>
              ) : (
                <>
                  Send message <FiArrowRight aria-hidden="true" />
                </>
              )}
            </button>
          </form>
        </div>
      </section>

      <section
        className="contact-faq page-shell"
        aria-labelledby="contact-faq-title"
      >
        <div className="contact-faq__heading">
          <p className="eyebrow">Frequently asked</p>
          <h2 id="contact-faq-title">
            A little help,
            <br />
            right away.
          </h2>
          <p>
            Still unsure? Write to us above and we will point you in the right
            direction.
          </p>
        </div>
        <div className="contact-faq__list">
          {faqItems.map(([question, answer], index) => (
            <details key={question}>
              <summary>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {question}
                <i aria-hidden="true" />
              </summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Contact;
